// Plain-text description of a saved artifact's query intent, with no LLM call — reopening a
// saved report starts a fresh conversation, and this is the only trace of what was loaded.
import { format, parseISO } from "date-fns";

import { describeFilters } from "./describeFilters";

type ArtifactSummaryInput = {
	name: string;
	outputType: "answer" | "table" | "chart";
	output?: { chartType?: string | null } | null;
	query?: {
		reportType?: string;
		summarizeBy?: unknown;
		aggregationOperations?: unknown;
		filters?: unknown;
	} | null;
	updatedAt?: string | Date;
	createdAt?: string | Date;
};

const AGGREGATION_VERBS: Record<string, string> = {
	count: "counting",
	sum: "summing",
	average: "averaging",
	minimum: "using the minimum of",
	maximum: "using the maximum of",
};

const asColumnId = (entry: unknown): string | undefined => {
	if (!entry || typeof entry !== "object") return undefined;
	const columnId = (entry as Record<string, unknown>).columnId;
	return typeof columnId === "string" ? columnId : undefined;
};

const uniqueColumnIds = (entries: unknown): string[] => {
	if (!Array.isArray(entries)) return [];
	return Array.from(new Set(entries.map(asColumnId).filter((id): id is string => !!id)));
};

const describeAggregation = (entry: unknown): string | undefined => {
	const columnId = asColumnId(entry);
	if (!columnId) return undefined;
	const operation = (entry as Record<string, unknown>).operation;
	const verb = (typeof operation === "string" && AGGREGATION_VERBS[operation]) || "aggregating";
	return `${verb} ${columnId}`;
};

export const buildArtifactSummary = (artifact: ArtifactSummaryInput): string => {
	const parts: string[] = [];

	const outputLabel =
		artifact.outputType === "chart"
			? artifact.output?.chartType
				? `${artifact.output.chartType} chart`
				: "chart"
			: artifact.outputType;

	parts.push(artifact.query?.reportType ? `${outputLabel} over ${artifact.query.reportType}` : outputLabel);

	const grouping = uniqueColumnIds(artifact.query?.summarizeBy);
	if (grouping.length > 0) parts.push(`grouped by ${grouping.join(", ")}`);

	const aggregationEntries = artifact.query?.aggregationOperations;
	const aggregations = Array.isArray(aggregationEntries)
		? aggregationEntries.map(describeAggregation).filter((entry): entry is string => !!entry)
		: [];
	if (aggregations.length > 0) parts.push(aggregations.join(", "));

	const filterDescription = describeFilters(artifact.query?.filters);
	if (filterDescription) parts.push(filterDescription);

	const summary = `Loaded saved report "${artifact.name}" — ${parts.join(", ")}. Ask me to refine it.`;

	if (artifact.outputType === "answer") {
		const savedAt = artifact.updatedAt ?? artifact.createdAt;
		if (savedAt) {
			const savedDate = typeof savedAt === "string" ? parseISO(savedAt) : savedAt;
			return `${summary} This is a text snapshot saved ${format(savedDate, "MMM d, yyyy")} — numbers reflect that date. Ask the question again for current data.`;
		}
	}

	return summary;
};

export default buildArtifactSummary;
